// The expanded model (spec/dashboard.md section 10.5): what an extension sees of a document — `$id`, `$unnyId`, `$type`,
// `$templateName`, every parameter under its stored camelCase key and a PascalCase alias, document references as the
// target's `$unnyId`, components nested with their own ids, localized strings as `GROUP/KEY`, products as objects — and
// the normalisation of a write back to the stored keys and values.
import type { Document, Json, Parameter, Template } from '../api/types';
import type { ContentCatalog } from '../content/ContentContext';
import type { DocumentLike } from '../content/format';
import { isComponentTarget } from '../content/widgets/context';
import { isTempId } from '../workspace/Workspace';

export type Expanded = Record<string, unknown> & { $id: string; $unnyId: string; $type: string; $templateName: string };

export interface ExpandContext {
  catalog: ContentCatalog;
  /** a document by id (drafts first), or undefined when the editor has not loaded it */
  resolve: (id: string) => DocumentLike | undefined;
  /** a string's GROUP/KEY by id when it is a staged draft the catalog does not have yet */
  stringKey?: (id: string) => string | undefined;
}

const MAX_DEPTH = 4;

export function pascal(name: string): string {
  return name.length ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

export function camel(name: string): string {
  return name.length ? name.charAt(0).toLowerCase() + name.slice(1) : name;
}

/** The `$unnyId` of a document: the legacy id when it has one, else the uuid (a temp id for a new draft). */
export function unnyIdOf(doc: Pick<DocumentLike, 'id' | 'unnyId'>): string {
  return doc.unnyId !== null && doc.unnyId !== undefined && String(doc.unnyId) !== '' ? String(doc.unnyId) : doc.id;
}

function expandValue(ctx: ExpandContext, p: Pick<Parameter, 'type' | 'template' | 'of'>, value: Json | undefined, depth: number): unknown {
  if (value === null || value === undefined) return null;
  switch (p.type) {
    case 'document': {
      if (typeof value !== 'string') return value;
      const target = ctx.resolve(value);
      if (target && isComponentTarget(ctx.catalog, p) && depth < MAX_DEPTH) return expandDocument(ctx, target, depth + 1);
      return target ? unnyIdOf(target) : value;
    }
    case 'localized_string': {
      if (typeof value !== 'string') return value;
      const s = ctx.catalog.strings.get(value);
      return s ? `${s.group}/${s.key}` : (ctx.stringKey?.(value) ?? value);
    }
    case 'object': {
      if (typeof value !== 'object' || Array.isArray(value)) return value;
      const inline = p.template ? ctx.catalog.templates.get(p.template) : undefined;
      return expandObject(ctx, inline, value as Record<string, Json>, depth + 1);
    }
    case 'list': {
      if (!Array.isArray(value)) return value;
      return value.map((v) => (p.of ? expandValue(ctx, p.of, v as Json, depth) : v));
    }
    case 'product':
      return value; // already { productId, amount, currency }
    default:
      return value;
  }
}

function expandObject(ctx: ExpandContext, template: Template | undefined, data: Record<string, Json>, depth: number): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (template) {
    out.$type = template.name;
    out.$templateName = template.name;
    for (const p of template.effectiveParameters) {
      const v = expandValue(ctx, p, data[p.name], depth);
      out[p.name] = v;
      const alias = pascal(p.name);
      if (alias !== p.name) out[alias] = v;
    }
    return out;
  }
  for (const [k, v] of Object.entries(data)) {
    out[k] = v;
    const alias = pascal(k);
    if (alias !== k) out[alias] = v;
  }
  return out;
}

/** The expanded model of a document (10.5). */
export function expandDocument(ctx: ExpandContext, doc: DocumentLike, depth = 0): Expanded {
  const template = ctx.catalog.templates.get(doc.template);
  const data = (doc.data ?? {}) as Record<string, Json>;
  const out = expandObject(ctx, template, data, depth) as Expanded;
  out.$id = doc.id;
  out.$unnyId = unnyIdOf(doc);
  out.$type = doc.template;
  out.$templateName = doc.template;
  return out;
}

/** The template a short or qualified name means: exact first; a short name by suffix (one template), else undefined; several suffix matches throw. */
export function resolveTemplateName(catalog: ContentCatalog, name: string): Template | undefined {
  const exact = catalog.templates.get(name);
  if (exact) return exact;
  const matches = catalog.templateList.filter((t) => t.name.endsWith(`.${name}`));
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];
  throw new Error(`Ambiguous template name '${name}': ${matches.map((t) => t.name).join(', ')}. Use the qualified name.`);
}

/** The parameter a written key names: the stored name, or its PascalCase / camelCase spelling. */
export function paramForKey(template: Template, key: string): Parameter | undefined {
  return template.effectiveParameters.find((p) => p.name === key) ?? template.effectiveParameters.find((p) => p.name === camel(key) || pascal(p.name) === key || p.name.toLowerCase() === key.toLowerCase());
}

export interface WriteContext {
  catalog: ContentCatalog;
  /** a string id by GROUP/KEY, creating the string when missing */
  stringIdFor: (fullKey: string) => string;
  /** a document id for a reference written as an id, a legacy id or an expanded object with `$unnyId` / `$id` */
  documentIdFor: (ref: unknown) => string | null;
}

/** A written value back to the stored shape of its parameter (10.4). */
export function normalizeValue(ctx: WriteContext, p: Pick<Parameter, 'type' | 'template' | 'of' | 'name'>, value: unknown): Json {
  if (value === null || value === undefined) return null;
  switch (p.type) {
    case 'localized_string': {
      if (typeof value !== 'string') throw new Error(`A localized string must be written as "GROUP/KEY"; got ${JSON.stringify(value)}`);
      if (ctx.catalog.strings.has(value)) return value;
      if (!value.includes('/')) throw new Error(`A localized string must be written as "GROUP/KEY"; got "${value}"`);
      return ctx.stringIdFor(value);
    }
    case 'document': {
      const id = ctx.documentIdFor(value);
      if (id === null) throw new Error(`Unknown document reference ${JSON.stringify(value)} for '${p.name}'.`);
      return id;
    }
    case 'product': {
      if (typeof value === 'string') {
        const product = ctx.catalog.productList.find((x) => x.itemId === value) ?? ctx.catalog.products.get(value);
        if (!product) throw new Error(`Unknown product '${value}'.`);
        return { productId: product.itemId, amount: Number(product.price.amount), currency: product.price.currency } as unknown as Json;
      }
      return value as Json;
    }
    case 'object': {
      if (typeof value !== 'object' || Array.isArray(value)) return value as Json;
      const inline = p.template ? ctx.catalog.templates.get(p.template) : undefined;
      if (!inline) return value as Json;
      const out: Record<string, Json> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (k.startsWith('$')) continue;
        const ip = paramForKey(inline, k);
        if (!ip) throw new Error(`Unknown parameter ${k} of template ${inline.name}`);
        out[ip.name] = normalizeValue(ctx, ip, v);
      }
      return out as unknown as Json;
    }
    case 'list': {
      if (!Array.isArray(value)) throw new Error(`'${p.name}' is a list; got ${JSON.stringify(value)}`);
      return value.map((v) => (p.of ? normalizeValue(ctx, { ...p.of, name: p.name }, v) : (v as Json))) as unknown as Json;
    }
    case 'int':
    case 'long':
    case 'float':
    case 'duration':
    case 'date_time':
    case 'enum':
      return typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)) ? Number(value) : (value as Json);
    default:
      return value as Json;
  }
}

/** The documents of a family with the drafts overlaid: new drafts added, modified overlaid, deletions removed. */
export function overlayDrafts(server: Document[], drafts: { id: string; status: string; current: Json; template?: string }[], family: Set<string>): Document[] {
  const byId = new Map(server.map((d) => [d.id, d]));
  for (const d of drafts) {
    if (!family.has(d.template ?? '')) continue;
    if (d.status === 'deleted') byId.delete(d.id);
    else byId.set(d.id, d.current as unknown as Document);
  }
  return Array.from(byId.values()).sort((a, b) => (isTempId(a.id) === isTempId(b.id) ? a.id.localeCompare(b.id) : isTempId(a.id) ? 1 : -1));
}

/** The TemplateDescriptor of 10.4. */
export function describeTemplate(catalog: ContentCatalog, t: Template): Record<string, unknown> {
  const kindWord: Record<string, string> = { document: 'Document', component: 'Component', singleton: 'Singleton', inline: 'Inline', profile: 'Profile', profile_root: 'ProfileRoot' };
  const typeWord: Record<string, string> = { int: 'Integer', long: 'Long', float: 'Float', bool: 'Boolean', string: 'String', localized_string: 'LocalizedString', enum: 'Enum', date_time: 'DateTime', duration: 'Duration', color: 'Color', document: 'EntityRef', object: 'EntityRef', list: 'List', asset: 'Asset', product: 'Product', template_ref: 'Template', profile_path: 'TemplateParam', any: 'Any', profile_data: 'ProfileData' };
  const sub = (p: Parameter): string | undefined => {
    if (p.type === 'document') return isComponentTarget(catalog, p) ? 'Component' : 'Document';
    if (p.type === 'object') return 'Inline';
    if (p.type === 'list') return p.of ? (p.of.type === 'document' ? (isComponentTarget(catalog, p.of) ? 'Component' : 'Document') : p.of.type === 'object' ? 'Inline' : 'Primitive') : 'Primitive';
    return undefined;
  };
  return {
    name: t.name,
    displayName: t.displayName ?? t.name,
    description: t.description ?? null,
    templateType: kindWord[t.kind] ?? t.kind,
    parentTemplate: t.parent ?? null,
    fields: t.effectiveParameters.map((p) => ({
      name: p.name,
      type: typeWord[p.type] ?? p.type,
      subType: sub(p),
      refTemplate: p.template ?? p.of?.template ?? undefined,
      enum: p.enum ?? p.of?.enum ?? undefined,
      required: Boolean(p.isRequired),
      description: p.description ?? null,
    })),
  };
}
